import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { withStyles, ThemeProvider } from '@material-ui/styles';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Done from '@material-ui/icons/Done';

import BusyButton from 'components/BusyButton/BusyButton';
import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import ORTypography from 'components/ORTypography/ORTypography';
import { required, maxLength } from 'services/validationMsgs';
import { lightTheme } from '../App/App.theme';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  logInAWS: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
  }).isRequired,
  valid: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  error: PropTypes.string,
};

const defaultProps = {
  error: null,
};

const maxLength128 = maxLength(128);

export function UnstyledAwsSignupPage({
  classes,
  handleSubmit,
  logInAWS,
  location,
  valid,
  submitting,
  error,
}) {
  const checkLowercase = new RegExp(/[a-z]/);
  const checkUppercase = new RegExp(/[A-Z]/);
  const checkNumbers = new RegExp(/[0-9]/);
  const checkSpecialChars = new RegExp(/[!@#$%^&*(),.?":{}|<>]/);

  const [hasEight, checkHasEight] = React.useState(false);
  const [hasUppercase, checkHasUppercase] = React.useState(false);
  const [hasLowercase, checkHasLowercase] = React.useState(false);
  const [hasNumsSpecialChars, checkHasNumsSpecialChars] = React.useState(false);
  const [validPassword, setValidPassword] = React.useState(false);

  const [terms, selectTerms] = React.useState(false);

  const checkPasswordValidity = (event) => {
    const string = event.currentTarget.value;
    checkHasEight(string.length >= 8);
    checkHasUppercase(checkUppercase.test(string));
    checkHasLowercase(checkLowercase.test(string));
    checkHasNumsSpecialChars(checkNumbers.test(string) || checkSpecialChars.test(string));

    if (
      string.length >= 8 &&
      checkUppercase.test(string) &&
      checkLowercase.test(string) &&
      (checkNumbers.test(string) || checkSpecialChars.test(string))
    ) {
      setValidPassword(true);
    } else {
      setValidPassword(false);
    }
  };

  const handleCheckBoxChange = () => {
    selectTerms(!terms);
  };

  const handleFormSubmissionReturn = (result) => {
    if (!result) return;
    throw new SubmissionError({
      _error: result.description || result.code || 'There was an error with your sign up.',
    });
  };

  const submitHandler = (data) => {
    const awsToken = location.search.split('=')[1];
    return logInAWS(data, awsToken).then(handleFormSubmissionReturn);
  };

  const errorMessageDisplay = (err) => (
    <div className={classes.errorTextHeader}>
      <span className={classes.errorText}>
        {err}
        &nbsp;Please contact&nbsp;
        <a
          href="https://objectrocket.zendesk.com/hc/en-us"
          target="_blank"
          rel="noopener noreferrer"
        >
          Support
        </a>
      </span>
    </div>
  );

  const wrappedIcon = (icon, source, altText) => (
    <div className={classes.fieldIcon}>{!icon ? <img src={source} alt={altText} /> : icon}</div>
  );

  const wrappedEmailIcon = wrappedIcon(<MailOutlineIcon color="secondary" />);
  const wrappedPasswordIcon = wrappedIcon(<LockOutlinedIcon color="secondary" />);
  const wrappedPersonIcon = wrappedIcon(
    false,
    'https://app.objectrocket.cloud/images/logos/lock_person_add_24px.svg',
    'Name',
  );

  const adornmentObject = (value, position) => ({
    value,
    position,
  });

  return (
    <div className={classes.container}>
      <div className={classes.contentContainer}>
        <div className={classes.textBlobContainer}>
          <ORTypography align="center" variant="h3" className={classes.welcomeText}>
            Welcome to&nbsp;
            <span className={classes.orangeText}>ObjectRocket</span>
          </ORTypography>
          <ORTypography align="center" variant="h5" className={classes.createLoginText}>
            Thank you for subscribing to ObjectRocket Datastores, please create a login to get
            started.
          </ORTypography>
        </div>

        <section className={classes.card}>
          <div className={classes.logo}>
            <img
              src="https://app.objectrocket.cloud/images/logos/or-logo.svg"
              alt="ObjectRocket logo"
            />
          </div>
          <div className={classes.formHeader}>
            <ORTypography className={classes.formHeaderText}>Sign Up</ORTypography>
          </div>
          <ThemeProvider theme={lightTheme}>
            <form className={classes.form} onSubmit={handleSubmit(submitHandler)}>
              <div className={classes.fieldContainer}>
                <Field
                  fullWidth
                  component={WrappedTextField}
                  className={classes.field}
                  variant="outlined"
                  placeholder="Email"
                  name="username"
                  label=""
                  validate={[required]}
                  adornment={adornmentObject(wrappedEmailIcon, 'start')}
                />
                <Field
                  fullWidth
                  component={WrappedTextField}
                  className={classes.field}
                  variant="outlined"
                  placeholder="Password"
                  name="password"
                  type="password"
                  label=""
                  onChange={checkPasswordValidity}
                  validate={[maxLength128, required]}
                  adornment={adornmentObject(wrappedPasswordIcon, 'start')}
                />
                <Field
                  fullWidth
                  component={WrappedTextField}
                  className={classes.field}
                  variant="outlined"
                  placeholder="First Name"
                  name="firstname"
                  label=""
                  validate={[required]}
                  adornment={adornmentObject(wrappedPersonIcon, 'start')}
                />
                <Field
                  fullWidth
                  component={WrappedTextField}
                  className={classes.field}
                  variant="outlined"
                  placeholder="Last Name"
                  name="lastname"
                  label=""
                  validate={[required]}
                  adornment={adornmentObject(wrappedPersonIcon, 'start')}
                />
              </div>
              <ORTypography variant="caption" className={classes.passwordRequirementText}>
                Passwords need to contain:
                <br />
                &nbsp;&nbsp;-&nbsp;at least 8 characters
                {hasEight && <Done classes={{ root: classes.checkmarkRoot }} fontSize="small" />}
                <br />
                &nbsp;&nbsp;-&nbsp;uppercase letters
                {hasUppercase && (
                  <Done classes={{ root: classes.checkmarkRoot }} fontSize="small" />
                )}
                <br />
                &nbsp;&nbsp;-&nbsp;lowercase letters
                {hasLowercase && (
                  <Done classes={{ root: classes.checkmarkRoot }} fontSize="small" />
                )}
                <br />
                &nbsp;&nbsp;-&nbsp;numbers or special characters
                {hasNumsSpecialChars && (
                  <Done classes={{ root: classes.checkmarkRoot }} fontSize="small" />
                )}
              </ORTypography>
              {!submitting && error && errorMessageDisplay(error)}
              <div className={classes.checkboxContainer}>
                <FormControlLabel
                  classes={{ root: classes.termsCheckboxControl }}
                  control={<Checkbox />}
                  onChange={handleCheckBoxChange}
                  checked={terms}
                  label={
                    <span>
                      I agree to the Objectrocket&nbsp;
                      <a
                        href="https://www.objectrocket.com/terms-of-service"
                        className={classes.termsLinkText}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </a>
                    </span>
                  }
                />
              </div>
              <BusyButton
                size="large"
                name="submit"
                type="submit"
                fullWidth
                loading={submitting}
                disabled={!valid || !terms || !validPassword}
                className={classes.button}
                variant="contained"
                color="primary"
              >
                <div className={classes.buttonText}>
                  Sign Up
                  <KeyboardArrowRightIcon />
                </div>
              </BusyButton>
            </form>
          </ThemeProvider>
        </section>
      </div>
    </div>
  );
}

UnstyledAwsSignupPage.propTypes = propTypes;
UnstyledAwsSignupPage.defaultProps = defaultProps;

const styles = (theme) => ({
  container: {
    overflow: 'hidden',
    background: 'radial-gradient(gray, #000)',
    backgroundSize: 'cover',
    height: '100%',
  },
  contentContainer: {
    position: 'absolute',
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  textBlobContainer: {
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'center',
    justifyContent: 'space-around',
    margin: theme.spacing(2),
  },
  welcomeText: {
    margin: theme.spacing(1),
  },
  orangeText: {
    color: theme.customColors.orange,
  },
  createLoginText: {
    margin: theme.spacing(1),
    fontWeight: '500',
  },
  field: {
    minHeight: theme.spacing(9.5),
  },
  fieldContainer: {
    margin: `0px ${theme.spacing(1)}px`,
    padding: `0px ${theme.spacing(1)}px`,
  },
  form: {
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
  },
  formHeader: {
    color: theme.customColors.mediumGrey,
    borderBottom: `1px solid ${theme.customColors.mediumGrey}`,
    textAlign: 'center',
    margin: `${theme.spacing(1)}px 0px`,
    paddingBottom: '8px',
  },
  card: {
    margin: 'auto',
    width: '350px',
    backgroundColor: '#fff',
  },
  checkboxContainer: {
    backgroundColor: theme.customColors.coolGrey,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  termsCheckboxControl: {
    '& span:first-child span': {},
    '& span:last-child': {
      fontSize: theme.spacing(1.75),
      fontWeight: '400',
      color: theme.customColors.black,
    },
  },
  button: {
    '& :disabled': {
      backgroundColor: theme.customColors.mediumGrey,
      borderRadius: 'unset',
    },
    '& :not(disabled)': {
      borderRadius: 'unset ',
    },
  },
  buttonText: {
    display: 'flex',
    alignItems: 'center',
    color: theme.customColors.white,
    minHeight: theme.spacing(8),
  },
  logo: {
    padding: '1.5rem 2.5rem',
    backgroundColor: theme.customColors.coolGrey,
  },
  fieldIcon: {
    backgroundColor: theme.customColors.coolGrey,
    padding: '13px',
    marginLeft: '-13.5px',
    borderBottomLeftRadius: '3px',
    borderTopLeftRadius: '3px',
  },
  termsLinkText: {
    textDecoration: 'none',
  },
  errorTextHeader: {
    textAlign: 'center',
  },
  errorText: {
    color: theme.customColors.red,
  },
  passwordRequirementText: {
    color: theme.customColors.black,
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
    lineHeight: '26px',
  },
  checkmarkRoot: {
    color: theme.palette.secondary.main,
    verticalAlign: 'text-bottom',
  },
});

export default withStyles(styles)(
  reduxForm({
    form: 'awsSignupForm',
    destroyOnUnmount: true,
  })(UnstyledAwsSignupPage),
);
